/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import useAlert from "../../hooks/Alert";
import BarcodeScanner from "../Common/BarcodeScanner";
import CustomInputField from "../InputFeilds/CustomInputField";
import Alert from "./Alert/Alert";
import TermsAndConditionsModal from "./modal/TermsAndConditionsModal";

// Capitalizes the first letter of each string
const capitalizeFirstLetter = (string) => string?.charAt(0).toUpperCase() + string.slice(1);

const SignUp = ({ baseURL, clientID, appSettings, AppDisplayName, setActiveTab }) => {
    const navigate = useNavigate();
    const { showAlert, setShowAlert, alertStyle, setAlertStyle, alertMessage, setAlertMessage, hideAlert } = useAlert();

    const { CountryResidence, CountryResidenceCode, Nationality, AppBaseURL1, ALLOWED_PREFIX, Mobile_length } =
        appSettings;

    // console.log("App Settings in SignUp:", appSettings);

    const mobilePrefix = appSettings?.SMS_SETTINGS?.ALLOWED_PREFIX || "";

    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [terms, setTerms] = useState(null);
    const [termsLoading, setTermsLoading] = useState(false);

    const inputFields = [
        {
            key: "cardNo",
            label: "Card No",
            type: "text",
            important: false,
            hide: true,
            readOnly: true,
        },
        {
            key: "fullName",
            label: "Full Name",
            type: "text",
            important: false,
            hide: false,
            customHandler: (value) => {
                // Split full name into first and last name
                const names = value.split(" ");
                const firstName = names[0] || "";
                const lastName = names.slice(1).join(" ") || "";
                return { firstName, lastName };
            },
        },
        {
            key: "gender",
            label: "Gender",
            type: "text",
            important: false,
            hide: true,
            defaultValue: "",
        },
        {
            key: "Date of birth",
            label: "Date of Birth",
            type: "date",
            important: false,
            hide: true,
            defaultValue: "",
        },
        {
            key: "email",
            label: "Email",
            type: "email",
            important: false,
            hide: false,
        },
        {
            key: "phoneNumber",
            label: "Mobile",
            type: "tel",
            important: false,
            hide: false,
        },
        {
            key: "nationality",
            label: "Nationality",
            type: "text",
            important: false,
            hide: true,
        },
        {
            key: "country",
            label: "Country of Residence",
            type: "text",
            important: false,
            hide: true,
        },
        {
            key: "city",
            label: "City",
            type: "text",
            important: false,
            hide: true,
        },
        {
            key: "area",
            label: "Area",
            type: "text",
            important: false,
            hide: true,
        },
        {
            key: "password",
            label: "Password",
            type: "password",
            important: false,
            hide: false,
        },
        {
            key: "confirmPassword",
            label: "Confirm Password",
            type: "password",
            important: false,
            hide: false,
        },
    ];

    // State variables for form fields and dynamic values
    const [formData, setFormData] = useState({
        cardNo: "",
        firstName: "",
        lastName: "",
        gender: "",
        "Date of birth": "",
        email: "",
        phoneNumber: "",
        nationality: Nationality || "",
        country: CountryResidence || "",
        city: "",
        area: "",
        password: "",
        confirmPassword: "",
    });
    const [loadingSignup, setLoadingSignup] = useState(false);
    const [countryCode, setCountryCode] = useState(CountryResidenceCode ? CountryResidenceCode : "");
    const [cityCode, setCityCode] = useState("");
    const [areaCode, setAreaCode] = useState("");
    const [options, setOptions] = useState({
        countries: [],
        cities: [],
        areas: [],
        nationalities: [],
        genders: [{ gender_name: "Male" }, { gender_name: "Female" }],
    });

    const [errors, setErrors] = useState({});

    // State for managing modal visibility of pickers
    const [isNationalityPickerOpen, setNationalityPickerOpen] = useState(false);
    const [isGenderPickerOpen, setGenderPickerOpen] = useState(false);
    const [isCityPickerOpen, setCityPickerOpen] = useState(false);
    const [isAreaPickerOpen, setAreaPickerOpen] = useState(false);
    const [isOptionsLoading, setOptionsLoading] = useState(false);

    const fetchOptions = async (type, payload, setter) => {
        setOptionsLoading(true);
        try {
            const response = await axiosInstance.post(`${appSettings.AppBaseURL1}CRM_DownloadCou_Ci_Ar`, payload, {
                headers: { "Content-Type": "application/json" },
            });

            // Use response.data directly, as axios automatically parses the response
            setter(response.data.DATA || []);
            setOptionsLoading(false);
        } catch (error) {
            setOptionsLoading(false);
            console.error(`Error loading ${type}:`, error);

            // Set the alert style based on error condition (adjusted to true/false)
            setAlertStyle({
                background: error
                    ? "linear-gradient(to right, #f6743e 0%, #d42525 100%)"
                    : "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
            });

            setAlertMessage({
                success: false,
                message: `Cannot load ${type}`,
                mode: `${type?.toUpperCase()}`,
            });

            setShowAlert("d-block");
        }
    };

    useEffect(() => {
        async function loadTerms() {
            setTermsLoading(true);

            const payLoad = {
                DATA: {
                    client_id: clientID,
                    type: "TERMS",
                },
            };

            try {
                const response = await axiosInstance.post(`${AppBaseURL1}Loy_getAboutUs`, payLoad, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (response.data?.STATUS) {
                    setTerms(response.data?.DATA); // Adjust based on API structure
                } else {
                    console.error("Failed to load terms:", response.data?.MESSAGE || "Unknown error");
                }
            } catch (error) {
                console.error("Error loading terms:", error);
            } finally {
                setTermsLoading(false);
            }
        }

        loadTerms();
    }, [clientID]);

    useEffect(() => {
        fetchOptions("countries", { DATA: { client_id: clientID, data_type: "M_COUNTRY", flag: "Y" } }, (data) =>
            setOptions((prev) => ({ ...prev, countries: data }))
        );
        fetchOptions("nationalities", { DATA: { client_id: clientID, data_type: "M_COUNTRY", flag: "ALL" } }, (data) =>
            setOptions((prev) => ({ ...prev, nationalities: data }))
        );
    }, [clientID]);

    useEffect(() => {
        if (formData.country) {
            fetchOptions(
                "cities",
                {
                    DATA: {
                        client_id: clientID,
                        data_type: "M_CITY",
                        country_code: countryCode || CountryResidenceCode,
                    },
                },
                (data) => setOptions((prev) => ({ ...prev, cities: data }))
            );
        }
    }, [countryCode]);

    useEffect(() => {
        if (formData.city) {
            fetchOptions(
                "areas",
                { DATA: { client_id: clientID, data_type: "M_AREA", country_code: countryCode, city_code: cityCode } },
                (data) => setOptions((prev) => ({ ...prev, areas: data }))
            );
        }
    }, [cityCode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "fullName") {
            const names = value.split(" ");
            const firstName = names[0] || "";
            const lastName = names.slice(1).join(" ") || "";

            setFormData((prev) => ({
                ...prev,
                fullName: value,
                firstName,
                lastName,
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName && !formData.lastName) newErrors.fullName = "Full Name is required";
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid Email Address";

        if (!formData.phoneNumber) {
            newErrors.phoneNumber = "Phone number is required.";
        } else if (formData.phoneNumber.length != Number(Mobile_length) && Mobile_length>0 && Mobile_length!=="") {
            const enteredLen = formData.phoneNumber.length;
            const expectedLen = Number(Mobile_length);

            newErrors.phoneNumber =
                enteredLen < expectedLen
                    ? `Phone number is too short. Please enter ${expectedLen} digits (Example: ${mobilePrefix}${"X".repeat(
                          expectedLen
                      )})`
                    : `Phone number is too long. Please enter only ${expectedLen} digits (Example: ${mobilePrefix}${"X".repeat(
                          expectedLen
                      )})`;
        }

        if (!formData.password) newErrors.password = "Password is required";
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords should match";
        // if (!formData.country) newErrors.country = "Country is required";
        // if (!formData.city) newErrors.city = "City is required";
        // if (!formData.area) newErrors.area = "Area is required";
        // if (!formData.nationality) newErrors.nationality = "Nationality is required";
        // if (!formData.gender) newErrors.gender = "Gender is required";
        // if (!formData["Date of birth"]) newErrors["Date of birth"] = "Date of birth is required";

        // const dob = new Date(formData["Date of birth"]);
        // const today = new Date();
        // const age = today.getFullYear() - dob.getFullYear();
        // const monthDiff = today.getMonth() - dob.getMonth();
        // const isBirthdayPassed = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= dob.getDate());

        // if (age < 18 || (age === 18 && !isBirthdayPassed)) {
        //     newErrors["Date of birth"] = "You must be 18 years or older";
        // }

        setErrors(newErrors);

        // Clear errors after 3 seconds
        if (Object.keys(newErrors).length > 0) {
            setTimeout(() => {
                setErrors({});
            }, 5000);
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log(validateForm(), "validation result");
        if (!validateForm()) return;

        if (!isTermsAccepted) {
            setAlertStyle({
                background: false
                    ? "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)"
                    : "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });

            setAlertMessage({
                success: false,
                message: "Please accept the terms and conditions",
                mode: "Terms and Conditions",
            });

            setShowAlert("d-block");

            const termsCheckbox = document.getElementById("terms");
            if (termsCheckbox) {
                termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        try {
            setLoadingSignup(true);
            const result = await sendSignUp(formData, clientID, countryCode, cityCode, areaCode, baseURL);
            const isSuccess = result?.STATUS;
            const message = result?.MESSAGE;

            setAlertStyle({
                background: isSuccess
                    ? "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)"
                    : "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });

            setAlertMessage({
                success: isSuccess,
                message: message || (isSuccess ? "Registration successful" : "Failed to register"),
                mode: "Sign Up",
            });

            setShowAlert("d-block");

            if (isSuccess) {
                setLoadingSignup(false);
                setTimeout(() => {
                    setActiveTab("0");
                    navigate("/login");
                }, 2000);
                setFormData({
                    cardNo: "",
                    firstName: "",
                    lastName: "",
                    gender: "",
                    "Date of birth": "",
                    email: "",
                    phoneNumber: "",
                    nationality: Nationality || "",
                    country: CountryResidence || "",
                    city: "",
                    area: "",
                    password: "",
                    confirmPassword: "",
                });
            } else {
                setLoadingSignup(false);
            }
        } catch (error) {
            setLoadingSignup(false);
            setAlertStyle({
                background: false
                    ? "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)"
                    : "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });

            setAlertMessage({
                success: false,
                message: "Error during sign-up",
                mode: "Sign Up",
            });

            setShowAlert("d-block");
            console.error("Error during sign-up:", error);
        } finally {
            setLoadingSignup(false);
        }
    };

    // Send data to backend for sign-up
    const sendSignUp = async (formData, clientID, countryCode, cityCode, areaCode, baseURL) => {
        const payLoad = {
            DIV_DES: "",
            DIV_ID: "1",
            FUNCTION: "LoyCreateCustomer",
            SEND_KEY: "123456",
            DATA: {
                AUTH_KEY: "TXlDb206TG95QVBJMTIz",
                TYPE_NAME: "CUSTOMER",
                CLIENT_ID: clientID,
                CUSTOMER_DETAILS: {
                    CARD_NO_INPUT: formData?.cardNo || "",
                    FIRSTNAME: capitalizeFirstLetter(formData.firstName),
                    LASTNAME: capitalizeFirstLetter(formData.lastName),
                    EMAIL: formData.email,
                    // MOBILE: mobilePrefix + formData.phoneNumber, // commented for prefix inclusion from input
                    MOBILE: formData.phoneNumber,
                    ADDR1: "addr1",
                    ADDR2: "addr1",
                    ADDR3: "addr3",
                    BIRTHDATE: formData["Date of birth"],
                    VALID: "1",
                    NATIONALITY: formData.nationality,
                    COUNTRY: formData.country,
                    GENDER: formData.gender,
                    CITY: formData.city,
                    AREA: formData.area,
                    country_code: countryCode,
                    city_code: cityCode,
                    area_code: areaCode,
                },
                LOGIN_DETAILS: {
                    USERNAME: formData.email,
                    PWORD: formData.password,
                    VALID: "Y",
                },
            },
        };
        // console.log("Payload for sign-up:", payLoad);
        try {
            const response = await axiosInstance.post(`${baseURL}/LoyCreateCustomer`, payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            // `response.data` will contain the parsed response from the server
            return response.data;
        } catch (error) {
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Failed to connect to the server.",
                mode: "Registration",
            });
            setShowAlert("d-block");
            console.error("Error in sendSignUp:", error);
        }
    };

    const handleSelect = (setter, field) => (value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        closePickers();
    };

    const handleSelectCode = (setter) => (value) => {
        console.log(value, "val");
        setter(value);
        closePickers();
    };

    const closePickers = () => {
        setGenderPickerOpen(false);
        setNationalityPickerOpen(false);
        setCityPickerOpen(false);
        setAreaPickerOpen(false);
    };

    const handleScanResult = async (barcodeValue) => {
        console.log("Scanned barcode value:", barcodeValue);
        if (!barcodeValue) return;
        try {
            // Make the API call with the scanned card number
            const response = await axiosInstance.post(
                `${baseURL}/Loy_GetCustomerByCardNo`,
                {
                    DATA: {
                        AUTH_KEY: "TXlDb206TG95QVBJMTIz",
                        CARD_NO: barcodeValue.card,
                        MOBILE_NO:barcodeValue.mob
                        // MOBILE_NO:barcodeValue.mob
                    },
                },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );



            const result = response.data; // Axios automatically parses the response

          
            if(result?.STATUS == 'SUCCESS'){
                    setAlertStyle({
                background:"linear-gradient(to right, #4caf50 0%, #2e7d32 100%)"
                    
            });

            setAlertMessage({
                success: true,
                message: "Loyalty card verified ✅ ",
                mode: "Welcome back!",
            });
             setShowAlert("d-block");
            }

            if (result?.STATUS !== "FAILURE") {
            
                // Populate the form data with the API response
                const customerData = result.DATA?.[0];
                setFormData((prev) => ({
                    ...prev,
                    cardNo: customerData?.card_No || prev.cardNo,
                    firstName: customerData?.first_name || prev.firstName,
                    lastName: customerData?.last_name || prev.lastName,
                    fullName: `${customerData?.first_name + " " + customerData?.last_name || ""}`,
                    gender: customerData?.gender || prev.gender,
                    "Date of birth": customerData?.birth_date?.split("T")[0] || prev["Date of birth"],
                    email: customerData?.Email || prev.email,
                    phoneNumber: customerData?.Mobile?.replace(mobilePrefix, "") || "" || prev.phoneNumber,
                    nationality: customerData?.nationality?.trim() || prev.nationality,
                }));
            } else {
                console.error("Error fetching customer data:", barcodeValue + " " + result?.MESSAGE);
                setFormData((prev) => ({
                    ...prev,
                    cardNo: "",
                    firstName: "",
                    lastName: "",
                    gender: "",
                    "Date of birth": "",
                    email: "",
                    phoneNumber: "",
                    fullName: "",
                }));
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: barcodeValue.card + " " + result.MESSAGE || "Failed to fetch customer data",
                    mode: "Barcode Scan",
                });
                setShowAlert("d-block");
            }
        } catch (error) {
            // console.error("Error during barcode API call:", error);
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Failed to connect to the server.",
                mode: "Barcode Scan",
            });
            setShowAlert("d-block");
        }
    };

    const handleResult=async(barcodeValue)=>{
            console.log("Scanned barcode value:", barcodeValue);
        if (!barcodeValue) return;
        try {
            // Make the API call with the scanned card number
            const response = await axiosInstance.post(
                `${baseURL}/Loy_GetCustomerByCardNo`,
                {
                    DATA: {
                        AUTH_KEY: "TXlDb206TG95QVBJMTIz",
                        CARD_NO: barcodeValue,
                        // MOBILE_NO:barcodeValue.mob
                        // MOBILE_NO:barcodeValue.mob
                    },
                },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const result = response.data; // Axios automatically parses the response

            if (result?.STATUS !== "FAILURE") {
                // Populate the form data with the API response
                const customerData = result.DATA?.[0];
                setFormData((prev) => ({
                    ...prev,
                    cardNo: customerData?.card_No || prev.cardNo,
                    firstName: customerData?.first_name || prev.firstName,
                    lastName: customerData?.last_name || prev.lastName,
                    fullName: `${customerData?.first_name + " " + customerData?.last_name || ""}`,
                    gender: customerData?.gender || prev.gender,
                    "Date of birth": customerData?.birth_date?.split("T")[0] || prev["Date of birth"],
                    email: customerData?.Email || prev.email,
                    phoneNumber: customerData?.Mobile?.replace(mobilePrefix, "") || "" || prev.phoneNumber,
                    nationality: customerData?.nationality?.trim() || prev.nationality,
                }));
            } else {
                console.error("Error fetching customer data:", barcodeValue + " " + result?.MESSAGE);
                setFormData((prev) => ({
                    ...prev,
                    cardNo: "",
                    firstName: "",
                    lastName: "",
                    gender: "",
                    "Date of birth": "",
                    email: "",
                    phoneNumber: "",
                    fullName: "",
                }));
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: barcodeValue + " " + result.MESSAGE || "Failed to fetch customer data",
                    mode: "Barcode Scan",
                });
                setShowAlert("d-block");
            }
        } catch (error) {
            // console.error("Error during barcode API call:", error);
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Failed to connect to the server.",
                mode: "Barcode Scan",
            });
            setShowAlert("d-block");
        }
    }


    const handleMobilePhoneResult=(data)=>{
      
        handleScanResult(data);
    }

    return (
        <div
            style={{
                height: "calc(100dvh - 110px)",
                width: "100%",
                position: "relative",
                background: "rgba(247, 246, 251, 1)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                scrollBehavior: "smooth",
            }}
        >
            {/* <PageTitle hideBackButton={false} name="SIGN UP"></PageTitle> */}
            <Alert showAlert={showAlert} alertStyle={alertStyle} alertMessage={alertMessage} hideAlert={hideAlert} />
            <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1rem 5rem" }}>
                 {/* <BarcodeScanner onScan={handleScanResult} getData={handleMobilePhoneResult}/> 
                    */}
                    <BarcodeScanner onScan={handleResult} getData={handleMobilePhoneResult}/>
                <form
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "5px",
                        padding: "0.5rem 0.25rem 0.8rem 0.25rem",
                        backgroundColor: "inherit",
                        gap: "8px",
                    }}
                >
                    {inputFields
                        .filter((field) => !field.hide)
                        .map((field) => (
                            <>
                                <CustomInputField
                                    defaultPrefix={mobilePrefix}
                                    key={field.key}
                                    disabled={field.readOnly}
                                    readOnly={field.readOnly}
                                    important={field.important}
                                    hideLabel={false}
                                    label={field.label}
                                    type={field.type}
                                    name={field.key}
                                    value={formData[field.key] || field.defaultValue || ""}
                                    onChange={handleInputChange}
                                    error={!!errors[field.key]}
                                    helperText={errors[field.key]}
                                    onFocus={() => {
                                        if (field.key === "gender") setGenderPickerOpen(true);
                                        else if (field.key === "nationality") setNationalityPickerOpen(true);
                                        else if (field.key === "city") setCityPickerOpen(true);
                                        else if (field.key === "area") setAreaPickerOpen(true);
                                    }}
                                />
                                {/* {!errors[field.key] && field.key === "phoneNumber" && <div style={{ color: "grey", fontSize: "12px", marginTop: "-8px" }}>Phone number must start with the prefix {mobilePrefix}.</div>} */}
                            </>
                        ))}
                    <div className="d-flex" style={{ margin: "0.5rem 0", gap: "0.25rem" }}>
                        {/* Checkbox and label grouped */}
                        <div
                            className="d-flex align-items-center gap-1"
                            style={{ margin: "0.5rem 0", flexWrap: "wrap" }}
                        >
                            <div className="d-flex align-items-center gap-1">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    name="terms"
                                    checked={isTermsAccepted}
                                    onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                                    style={{ margin: "0.5rem 0" }}
                                />
                                <label
                                    htmlFor="terms"
                                    style={{ fontSize: "13.5px", color: "black", marginLeft: "4px" }}
                                >
                                    By signing up, you are agreeing to our&nbsp;
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsTermsModalOpen(true);
                                        }}
                                        style={{ color: "#00008b", textDecoration: "underline", fontSize: "13.5px" }}
                                    >
                                        terms and conditions
                                    </a>
                                    <span style={{ fontSize: "13.5px" }}>&nbsp;of use.</span>
                                </label>
                            </div>

                            {
                                <TermsAndConditionsModal
                                    isOpen={isTermsModalOpen}
                                    onClose={() => setIsTermsModalOpen(false)}
                                    onAccept={() => {
                                        setIsTermsModalOpen(false);
                                        setIsTermsAccepted(true);
                                    }}
                                    terms={terms}
                                    loading={termsLoading}
                                />
                            }
                        </div>

                        {/* Terms Modal */}
                        {
                            <TermsAndConditionsModal
                                isOpen={isTermsModalOpen}
                                onClose={() => setIsTermsModalOpen(false)}
                                onAccept={() => {
                                    setIsTermsModalOpen(false);
                                    setIsTermsAccepted(true);
                                }}
                                terms={terms}
                                loading={termsLoading}
                            />
                        }
                    </div>
                </form>
            </div>

            <div
                className="max-width-700"
                style={{
                    position: "fixed",
                    bottom: "1%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "92%",
                    backgroundColor: "none",
                }}
            >
                <button
                    className="btn btn-primary bg-wafaa-purple font-size-16"
                    onClick={handleSubmit}
                    disabled={loadingSignup}
                    style={{
                        width: "100%",
                        height: "45px",
                        color: "white",
                        textTransform: "capitalize",
                        marginBlock: ".5rem",
                        border: "none",
                        cursor: "pointer",
                        borderRadius: "5px",
                    }}
                >
                    {loadingSignup ? "Registering..." : "Register"}
                </button>
            </div>

            {/* {alert.visible && <div>{alert.message}</div>} */}

            {/* Pickers for selecting nationality, gender, city, and area */}
            {/* <IOSPicker
                options={options.nationalities}
                selectedValue={formData.nationality}
                selectedName={formData.nationality}
                onSelect={handleSelect(setFormData, "nationality")}
                openModal={isNationalityPickerOpen}
                title={"Nationality"}
                handleClose={closePickers}
                keyName={"country_name"}
            /> */}
            {/* <IOSPicker
                options={options.genders}
                selectedValue={formData.gender}
                onSelect={handleSelect(setFormData, "gender")}
                openModal={isGenderPickerOpen}
                title={"Gender"}
                handleClose={closePickers}
                keyName={"gender_name"}
            /> */}
            {/* <IOSPicker
                options={options.cities}
                selectedValue={formData.city}
                onSelect={handleSelect(setFormData, "city")}
                onSelectCode={handleSelectCode(setCityCode)}
                openModal={isCityPickerOpen}
                title={"City"}
                handleClose={closePickers}
                keyCode={"city_code"}
                keyName={"city_name"}
            /> */}
            {/* <IOSPicker
                options={options.areas}
                selectedValue={formData.area}
                onSelect={handleSelect(setFormData, "area")}
                onSelectCode={handleSelectCode(setAreaCode)}
                openModal={isAreaPickerOpen}
                title={"Area"}
                handleClose={closePickers}
                keyCode={"area_code"}
                keyName={"area_name"}
            /> */}
        </div>
    );
};

export default SignUp;
